import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Restaurants from './pages/Restaurants';
import Students from './pages/Students';
import StudentProfile from './pages/StudentProfile';
import StudentRecords from './pages/StudentRecords';
import Placements from './pages/Placements';
import TaskCalendar from './pages/TaskCalendar';
import ContractTemplates from './pages/ContractTemplates';
import ContractSigningPage from './pages/ContractSigningPage';
import EmailTemplates from './pages/EmailTemplates';
import AssessmentRules from './pages/AssessmentRules';
import PlacementDocuments from './pages/PlacementDocuments';
import CycleExports from './pages/CycleExports';
import Cycles from './pages/Cycles';
import CycleDetail from './pages/CycleDetail';
import PhaseDetail from './pages/PhaseDetail';
import UserManagement from './pages/UserManagement';
import Settings from './pages/Settings';
import FeedbackTemplates from './pages/FeedbackTemplates';
import FeedbackTemplateBuilder from './pages/FeedbackTemplateBuilder';
import PlacementFeedback from './pages/PlacementFeedback';
import AllFeedback from './pages/AllFeedback';
import PerformanceReport from './pages/PerformanceReport';
import FeedbackFormView from './pages/FeedbackFormView';
import ChefFeedbackForm from './pages/ChefFeedbackForm';
import PlacementAcknowledgment from './pages/PlacementAcknowledgment';
import './ica-theme.css';

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f7fa]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public route wrapper (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f7fa]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/restaurants"
        element={
          <ProtectedRoute>
            <Restaurants />
          </ProtectedRoute>
        }
      />
      <Route
        path="/students"
        element={
          <ProtectedRoute>
            <Students />
          </ProtectedRoute>
        }
      />
      <Route
        path="/students/:studentId"
        element={
          <ProtectedRoute>
            <StudentProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student-records"
        element={
          <ProtectedRoute>
            <StudentRecords />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student-records/:studentId"
        element={
          <ProtectedRoute>
            <StudentProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/placements"
        element={
          <ProtectedRoute>
            <Placements />
          </ProtectedRoute>
        }
      />
      <Route
        path="/placement-calendar"
        element={
          <ProtectedRoute>
            <TaskCalendar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <TaskCalendar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contract-templates"
        element={
          <ProtectedRoute>
            <ContractTemplates />
          </ProtectedRoute>
        }
      />
      <Route path="/contract/sign/:token" element={<ContractSigningPage />} />
      <Route
        path="/email-templates"
        element={
          <ProtectedRoute>
            <EmailTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessment-rules"
        element={
          <ProtectedRoute>
            <AssessmentRules />
          </ProtectedRoute>
        }
      />
      <Route
        path="/placements/documents"
        element={
          <ProtectedRoute>
            <PlacementDocuments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compliance"
        element={
          <ProtectedRoute>
            <PlacementDocuments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cycle-exports"
        element={
          <ProtectedRoute>
            <CycleExports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cycles"
        element={
          <ProtectedRoute>
            <Cycles />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cycles/:id"
        element={
          <ProtectedRoute>
            <CycleDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cycles/:id/phases/:phaseId"
        element={
          <ProtectedRoute>
            <PhaseDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feedback-templates"
        element={
          <ProtectedRoute>
            <FeedbackTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feedback-templates/new"
        element={
          <ProtectedRoute>
            <FeedbackTemplateBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feedback-templates/:templateId"
        element={
          <ProtectedRoute>
            <FeedbackTemplateBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/placements/:placementId/feedback"
        element={
          <ProtectedRoute>
            <PlacementFeedback />
          </ProtectedRoute>
        }
      />
      <Route
        path="/placement-feedback"
        element={
          <ProtectedRoute>
            <AllFeedback />
          </ProtectedRoute>
        }
      />
      <Route
        path="/students/:studentId/performance"
        element={
          <ProtectedRoute>
            <PerformanceReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feedback-view/:formId"
        element={
          <ProtectedRoute>
            <FeedbackFormView />
          </ProtectedRoute>
        }
      />

      {/* Public routes for chef feedback & student acknowledgment */}
      <Route path="/feedback/:token" element={<ChefFeedbackForm />} />
      <Route path="/placement-ack/:token" element={<PlacementAcknowledgment />} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
